import { useState, useEffect, useCallback } from "react";

import { ClientCertificate as Component } from "../../components/organisms/ClientCertificate";
import { useLazyQuery } from "@apollo/client/react";
import { GQLQuery } from "@rx-cs-system/schema";
import { GET_CLIENT_CERTIFICATE } from "../../graphql/queries";
import { useSnackBar, useCheckUserRole } from "../../hooks";
import type { ClientCertificate as ClientCertificateType } from "../../types";

type ClientCertificateProps = {
  clientCertificate: Pick<ClientCertificateType, "domain" | "name">;
};

export function ClientCertificate({
  clientCertificate,
}: ClientCertificateProps) {
  useCheckUserRole();
  const { snackBar, showOKSnackBar, showErrorSnackBar } = useSnackBar();
  const [modal, setModal] = useState<{
    isOpen: boolean;
    password: string;
  }>({
    isOpen: false,
    password: "",
  });
  const openModal = useCallback(
    (password: string) => {
      setModal({
        isOpen: true,
        password,
      });
    },
    [setModal],
  );
  const closeModal = useCallback(() => {
    setModal({
      isOpen: false,
      password: "",
    });
  }, [setModal]);

  const [getClientCertificate, { loading, data, error }] =
    useLazyQuery<GQLQuery>(GET_CLIENT_CERTIFICATE);

  const handleGetClientCertificatePassword = async () => {
    getClientCertificate({
      variables: {
        domain: clientCertificate.domain,
      },
    });
  };

  const handleCopyPasswordToClipboard = (text: string) => {
    if (!navigator.clipboard) {
      showErrorSnackBar("このブラウザはクリップボードコピーに対応していません");
      closeModal();
      return;
    }
    navigator.clipboard.writeText(text).then(
      () => {
        closeModal();
        showOKSnackBar("パスワードをクリップボードにコピーしました");
      },
      () => {
        showErrorSnackBar("パスワードのコピーに失敗しました");
      },
    );
  };

  useEffect(() => {
    if (!loading && data?.clientCertificate?.password) {
      openModal(data.clientCertificate.password);
    }
  }, [data, loading, openModal]);

  useEffect(() => {
    if (!loading && error) {
      showErrorSnackBar(
        "パスワードの取得に失敗しました。DTFチームにお問い合わせください。",
      );
      closeModal();
    }
  }, [error, loading, showErrorSnackBar, closeModal]);

  return (
    <Component
      isOpen={modal.isOpen}
      clientCertificate={clientCertificate}
      snackBar={snackBar}
      onCancelButtonClick={closeModal}
      closeModal={closeModal}
      loading={loading}
      password={modal.password}
      handleGetClientCertificatePassword={handleGetClientCertificatePassword}
      handleCopyPasswordToClipboard={handleCopyPasswordToClipboard}
    />
  );
}
